/**
 * DiwaliDecor — sparse, non-interactive Diwali lamps on the viewport edges.
 *
 * Window (Asia/Kolkata): 2026-10-30 00:00 IST (inclusive) to
 * 2026-11-13 00:00 IST (exclusive), i.e. through 12 Nov 2026 inclusive.
 * Purely additive: fixed position, pointer-events none, no body padding,
 * hidden in print, static under prefers-reduced-motion.
 */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import "./DiwaliDecor.css";

export const DIWALI_START_ISO = "2026-10-30T00:00:00+05:30";
export const DIWALI_END_ISO = "2026-11-13T00:00:00+05:30"; // exclusive
export const DIWALI_START_MS = Date.parse(DIWALI_START_ISO);
export const DIWALI_END_MS = Date.parse(DIWALI_END_ISO);
// setTimeout caps at ~24.8 days; re-arm at most hourly for safety.
export const DIWALI_MAX_TIMER_MS = 60 * 60 * 1000;

export function isDiwaliActive(date = new Date()) {
  const t = date instanceof Date ? date.getTime() : Number(date);
  if (!Number.isFinite(t)) return false;
  return t >= DIWALI_START_MS && t < DIWALI_END_MS;
}

/** ms until the next boundary that would flip state, bounded; null if none. */
export function msUntilNextDiwaliBoundary(now = Date.now()) {
  let next = null;
  if (now < DIWALI_START_MS) next = DIWALI_START_MS;
  else if (now < DIWALI_END_MS) next = DIWALI_END_MS;
  if (next === null) return null;
  return Math.max(0, Math.min(next - now, DIWALI_MAX_TIMER_MS));
}

const GOLD = "#F2C66D";

function Lantern({ id, className, delay = 0, hue = "maroon" }) {
  const body = hue === "maroon" ? ["#9E2B3A", "#C2413F"] : ["#C7561F", "#E8892B"];
  return (
    <span className={`flg-diwali-lantern ${className}`} style={{ "--flg-dw-delay": `${delay}s` }}>
      <svg viewBox="0 0 60 150" width="100%" height="100%" focusable="false" aria-hidden="true">
        <defs>
          <linearGradient id={`flg-dw-lg-${id}`} x1="0" x2="1">
            <stop offset="0" stopColor={body[0]} />
            <stop offset="0.5" stopColor={body[1]} />
            <stop offset="1" stopColor={body[0]} />
          </linearGradient>
        </defs>
        <line x1="30" y1="0" x2="30" y2="40" stroke="#8A5A2B" strokeWidth="1.2" />
        <circle cx="30" cy="40" r="3" fill={GOLD} />
        <ellipse className="flg-diwali-lantern__glow" cx="30" cy="80" rx="27" ry="30" fill="url(#flg-dw-halo)" />
        <path d="M18 46 H42 L46 54 H14 Z" fill={GOLD} />
        <path d="M14 54 C2 66 2 94 14 106 H46 C58 94 58 66 46 54 Z" fill={`url(#flg-dw-lg-${id})`} />
        <path d="M30 54 V106 M20 56 C14 72 14 88 20 104 M40 56 C46 72 46 88 40 104" stroke={GOLD} strokeWidth="1" fill="none" opacity="0.8" />
        <path d="M30 68 L36 80 L30 92 L24 80 Z" fill="#FFE7A8" className="flg-diwali-lantern__core" />
        <path d="M14 54 H46 M12 80 H48 M14 106 H46" stroke={GOLD} strokeWidth="1.6" />
        <circle cx="20" cy="80" r="1.6" fill={GOLD} /><circle cx="40" cy="80" r="1.6" fill={GOLD} />
        <path d="M16 106 H44 L38 114 H22 Z" fill={GOLD} />
        <g stroke={GOLD} strokeWidth="1.2">
          <line x1="22" y1="114" x2="20" y2="138" /><line x1="26" y1="114" x2="25" y2="142" />
          <line x1="30" y1="114" x2="30" y2="146" /><line x1="34" y1="114" x2="35" y2="142" />
          <line x1="38" y1="114" x2="40" y2="138" />
        </g>
        <circle cx="30" cy="146" r="2.4" fill="#C2413F" />
      </svg>
    </span>
  );
}

function Diya({ delay = 0, small = false }) {
  return (
    <span className={`flg-diwali-diya${small ? " flg-diwali-diya--sm" : ""}`} style={{ "--flg-dw-delay": `${delay}s` }}>
      <svg viewBox="0 0 64 64" width="100%" height="100%" focusable="false" aria-hidden="true">
        <ellipse className="flg-diwali-halo" cx="32" cy="20" rx="18" ry="20" fill="url(#flg-dw-halo)" />
        <g className="flg-diwali-flame">
          <path d="M32 4 C38 13 39 20 32 29 C25 20 26 13 32 4 Z" fill="#F7A531" />
          <path d="M32 12 C35 18 35 22 32 27 C29 22 29 18 32 12 Z" fill="#FFF1C4" />
        </g>
        <path d="M6 33 H58 C56 46 45 53 32 53 C19 53 8 46 6 33 Z" fill="#A83E26" />
        <path d="M6 33 H58 C57.4 36 56.4 38.5 55 40.5 H9 C7.6 38.5 6.6 36 6 33 Z" fill="#D9792E" />
        <path d="M12 44 Q18 40.5 24 44 T36 44 T48 44 T54 43" stroke={GOLD} strokeWidth="1.6" fill="none" strokeLinecap="round" />
        <circle cx="32" cy="48" r="1.8" fill={GOLD} /><circle cx="21" cy="47" r="1.2" fill={GOLD} /><circle cx="43" cy="47" r="1.2" fill={GOLD} />
        <rect x="23" y="53" width="18" height="3.4" rx="1.7" fill="#6E2619" />
      </svg>
    </span>
  );
}

function Toran() {
  const drops = Array.from({ length: 13 });
  return (
    <div className="flg-diwali-toran">
      <svg className="flg-diwali-toran__cord" viewBox="0 0 100 6" preserveAspectRatio="none" aria-hidden="true" focusable="false">
        <path d="M0 1 Q12.5 6 25 1 T50 1 T75 1 T100 1" stroke="#E8892B" strokeWidth="1.6" fill="none" vectorEffect="non-scaling-stroke" />
      </svg>
      {drops.map((_, i) => (
        <span key={i} className="flg-diwali-toran__drop" style={{ left: `${2 + i * 8}%`, "--flg-dw-delay": `${(i % 4) * 0.5}s` }}>
          <svg viewBox="0 0 12 26" width="11" height="24" aria-hidden="true" focusable="false">
            <circle cx="6" cy="4.5" r="4.2" fill={i % 2 ? "#E8892B" : "#F2C14E"} />
            <circle cx="6" cy="11.5" r="3.4" fill={i % 2 ? "#F2C14E" : "#E8892B"} />
            <path d="M6 15 C9.5 18.5 9 23 6 25.5 C3 23 2.5 18.5 6 15 Z" fill="#3E7A4E" />
          </svg>
        </span>
      ))}
    </div>
  );
}

function Bloom({ className, delay }) {
  const rays = Array.from({ length: 12 });
  return (
    <span className={`flg-diwali-bloom ${className}`} style={{ "--flg-dw-delay": `${delay}s` }}>
      <svg viewBox="-50 -50 100 100" width="100%" height="100%" aria-hidden="true" focusable="false">
        {rays.map((_, i) => (
          <g key={i} transform={`rotate(${i * 30})`}>
            <line x1="0" y1="-14" x2="0" y2="-38" stroke={i % 2 ? "#F2C14E" : "#E8892B"} strokeWidth="2" strokeLinecap="round" />
            <circle cx="0" cy="-43" r="2.4" fill={i % 3 ? GOLD : "#E86A5A"} />
          </g>
        ))}
        <circle r="4" fill="#FFF1C4" />
      </svg>
    </span>
  );
}

const SPARKS = [
  ["flg-diwali-spark--1", 0], ["flg-diwali-spark--2", 1.1], ["flg-diwali-spark--3", 2.3],
  ["flg-diwali-spark--4", 0.6], ["flg-diwali-spark--5", 1.8], ["flg-diwali-spark--6", 2.9],
];

function Spark({ className, delay }) {
  return (
    <span className={`flg-diwali-spark ${className}`} style={{ "--flg-dw-delay": `${delay}s` }}>
      <svg viewBox="-10 -10 20 20" width="100%" height="100%" aria-hidden="true" focusable="false">
        <path d="M0 -10 L2 -2 L10 0 L2 2 L0 10 L-2 2 L-10 0 L-2 -2 Z" fill="#F7C65A" />
      </svg>
    </span>
  );
}

/** Shared season lifecycle: bounded expiry timer + focus/visibility recheck. */
export function useDiwaliActive() {
  const [active, setActive] = useState(() => isDiwaliActive(new Date()));

  useEffect(() => {
    let timer = null;
    const recheck = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      const now = Date.now();
      setActive(isDiwaliActive(new Date(now)));
      const wait = msUntilNextDiwaliBoundary(now);
      if (wait !== null) timer = setTimeout(recheck, wait + 50);
    };
    recheck();
    const onVis = () => { if (document.visibilityState !== "hidden") recheck(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", recheck);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", recheck);
    };
  }, []);
  return active;
}

/** Pair of firework blooms anchored inside the SmartDashboard greeting header. */
export function DiwaliDashboardBlooms() {
  const active = useDiwaliActive();
  if (!active) return null;
  return (
    <div className="flg-diwali-dash-blooms" aria-hidden="true" data-testid="diwali-dashboard-blooms">
      <Bloom className="flg-diwali-bloom--dash-a" delay={0.6} />
      <Bloom className="flg-diwali-bloom--dash-b" delay={5.1} />
    </div>
  );
}

export default function DiwaliDecor() {
  const { pathname } = useLocation();
  const welcomePage = ["/login", "/register", "/forgot-password", "/reset-password"].includes(pathname);
  const active = useDiwaliActive();

  if (!active) return null;

  return (
    <div className={`flg-diwali-root${welcomePage ? "" : " flg-diwali-root--workspace"}`}
      aria-hidden="true" data-testid="diwali-decor">
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true" focusable="false">
        <defs>
          <radialGradient id="flg-dw-halo">
            <stop offset="0%" stopColor="#FFC85A" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#FFC85A" stopOpacity="0" />
          </radialGradient>
        </defs>
      </svg>
      <Toran />
      <Lantern id="l1" className="flg-diwali-lantern--l1" delay={0} hue="maroon" />
      <Lantern id="r1" className="flg-diwali-lantern--r1" delay={0.9} hue="saffron" />
      <Lantern id="r2" className="flg-diwali-lantern--r2" delay={1.7} hue="maroon" />
      <Bloom className="flg-diwali-bloom--a" delay={0} />
      <Bloom className="flg-diwali-bloom--b" delay={4.5} />
      {SPARKS.map(([c, d]) => <Spark key={c} className={c} delay={d} />)}
      <div className="flg-diwali-cluster flg-diwali-cluster--left">
        <Diya small delay={0.4} /><Diya delay={0} /><Diya small delay={1.1} />
      </div>
      <div className="flg-diwali-cluster flg-diwali-cluster--right">
        <Diya small delay={0.8} /><Diya delay={0.3} /><Diya small delay={1.5} />
      </div>
    </div>
  );
}
